import { SignableSchema, SignerSchema } from '../definitions/schemas';
import { SignableProviders } from './SignableProvider';

export enum AuditLogEvent {
  SIGNED = 'signed',
  VIEWED = 'viewed',
}

/**
 * @see https://github.com/referenceable/referenceable_api/blob/develop/app/models/signable.rb
 */
export enum SignableStatus {
  COMPLETED = 'completed',
  CREATED = 'created',
  DECLINED = 'declined',
  DELETED = 'deleted',
  DELIVERED = 'delivered',
  SENT = 'sent',
  SIGNED = 'signed',
  VOIDED = 'voided',
}

export enum SignableSyncStatus {
  OK = 'ok',
  DISCONNECTED = 'disconnected',
  PROBLEM = 'problem',
}

export enum SignerStatus {
  AUTORESPONDED = 'autoresponded',
  COMPLETED = 'completed',
  CREATED = 'created',
  DECLINED = 'declined',
  DELIVERED = 'delivered',
  FAXPENDING = 'faxpending',
  SENT = 'sent',
  SIGNED = 'signed',
}

export enum SignableTemplatesAction {
  Create = 'create',
  Edit = 'edit',
}

export const COMPLETED_SIGNATURE_STATUS = [
  SignerStatus.COMPLETED,
  SignerStatus.SIGNED,
];

export const SIGNABLE_ERROR_STATUS = [
  SignableStatus.DECLINED,
  SignableStatus.VOIDED,
  SignableSyncStatus.DISCONNECTED,
  SignableSyncStatus.PROBLEM,
];

/**
 * Splits the signable status into the 3 most important statuses, being:
 *  1. Has an error status, which would be declined or voided
 *  2. Has a connection error (sync) status, which would be if the signable
 *     hasn't been completed and there's a problem with the connection or
 *     that it has been disconnected; as the connection becomes irrelevant
 *     once it's been completed
 *  3. Whether it's been completed or not
 */
export const getSignableStatus = (
  status?: SignableStatus,
  syncStatus?: SignableSyncStatus
): [boolean, boolean, boolean] => {
  const isCompleted = status === SignableStatus.COMPLETED;

  return [
    !!status && SIGNABLE_ERROR_STATUS.includes(status),
    !!syncStatus && !isCompleted && SIGNABLE_ERROR_STATUS.includes(syncStatus),
    isCompleted,
  ];
};

/**
 * Abstraction for the logic of deleting an inhouse signable document, which
 * needs to be voided only if it hasn't been accessed by one of the parties
 * and it's currently on a status that can be voided
 */
export const isVoidingRequired = (signable: SignableSchema) =>
  signable.provider === SignableProviders.EnableUs &&
  signable.canBeVoided &&
  (signable.status !== SignableStatus.CREATED ||
    isDocumentViewed(signable.signers));

export const isDocumentViewed = (signers: SignerSchema[]) =>
  signers.some(({ lastEvent }) =>
    [AuditLogEvent.SIGNED, AuditLogEvent.VIEWED].includes(
      (lastEvent || '') as AuditLogEvent
    )
  );
